import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles, Award, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  const highlights = [
  {
    icon: <Award className="w-8 h-8" />,
    title: 'Established Since 2010',
    description: 'Over a decade of trusted brand distribution in Mongolia'
  },
  {
    icon: <CheckCircle2 className="w-8 h-8" />,
    title: 'Official Brand Distribution',
    description: 'Authorized distributor for premium international brands'
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: 'Retail Presence in Ulaanbaatar',
    description: '6 strategic locations across the city'
  },
  {
    icon: <Sparkles className="w-8 h-8" />,
    title: 'Quality & Customer Experience',
    description: 'Commitment to excellence in every interaction'
  },
  {
    icon: <TrendingUp className="w-8 h-8" />,
    title: 'Sustainability & Innovation',
    description: 'Forward-thinking approach to wellness and lifestyle'
  }];


  const brands = [
  {
    name: 'TONYMOLY',
    tagline: 'Modern Korean Beauty',
    description: 'Modern South Korean beauty brand known for innovative skincare, effective formulas, and creative design.',
    image: '/images/tonymoly-brand.webp',
    link: '/brands#tonymoly'
  },
  {
    name: 'Portmeirion',
    tagline: 'British Elegance',
    description: 'Prestigious British homeware brand known for timeless design, craftsmanship, and elegant lifestyle collections.',
    image: '/images/portmeirion-brand.webp',
    link: '/brands#portmeirion'
  },
  {
    name: 'Ecover',
    tagline: 'Clean Air & Wellness',
    description: 'Sustainability-driven brand focused on clean air solutions and healthier indoor living.',
    image: '/images/ecover-brand.jpg',
    link: '/brands#ecover'
  }];


  const whyAmarStars = [
  {
    title: 'Trusted Partnerships',
    description: 'We work with globally recognized brands with strong identity and proven quality.'
  },
  {
    title: 'Retail Experience',
    description: 'We create refined in-store and brand experiences beyond simple distribution.'
  },
  {
    title: 'Design-Led Thinking',
    description: 'We believe presentation, detail, and storytelling matter.'
  },
  {
    title: 'Long-Term Vision',
    description: 'We build brands with sustainability and lasting customer trust.'
  }];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Multi-Brand Collage */}
        <div className="absolute inset-0 z-0">
          <div className="relative w-full h-full grid grid-cols-3 gap-0">
            {/* TONYMOLY Section */}
            <div className="relative overflow-hidden">
              <img
                src="/images/tonymoly-brand.webp"
                alt="TONYMOLY Products"
                className="w-full h-full object-cover" />

            </div>
            {/* Portmeirion Section */}
            <div className="relative overflow-hidden">
              <img
                src="/images/portmeirion-brand.webp"
                alt="Portmeirion Products"
                className="w-full h-full object-cover" />

            </div>
            {/* Ecover Section */}
            <div className="relative overflow-hidden">
              <img
                src="/images/ecover-brand.jpg"
                alt="Ecover Products"
                className="w-full h-full object-cover" />

            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/50"></div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl space-y-8">
            <h1 className="!font-extrabold !text-4xl text-white">
              Bringing Beauty, Lifestyle & Wellness Brands to Mongolia
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed font-light">
              Amar Stars LLC has been connecting Mongolian consumers with trusted international brands since 2010 through thoughtful distribution and retail excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/brands">
                <Button className="bg-white text-black hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-bold transition-all duration-300 hover:scale-105">
                  Explore Our Brands
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300">

                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-2xl md:text-3xl text-gray-800 leading-relaxed font-light">
              Amar Stars LLC is a <span className="font-semibold">Ulaanbaatar-based importer and distributor</span> of international brands, focused on delivering high-quality products and refined customer experiences across beauty, home, and wellness categories.
            </p>
            
            {/* Brand Logos Strip */}
            <div className="pt-8">
              <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16">
                {/* TONYMOLY Logo */}
                <div className="flex items-center justify-center h-16 md:h-20">
                  <img
                    src="/images/tonymoly-logo-new.png"
                    alt="TONYMOLY"
                    className="h-full w-auto object-contain"
                    style={{ maxWidth: '180px' }}
                  />
                </div>
                
                {/* Portmeirion Logo */}
                <div className="flex items-center justify-center h-16 md:h-20">
                  <img
                    src="/images/portmeirion-logo-new.png"
                    alt="Portmeirion"
                    className="h-full w-auto object-contain"
                    style={{ maxWidth: '180px' }}
                  />
                </div>
                
                {/* Ecover Logo */}
                <div className="flex items-center justify-center h-16 md:h-20">
                  <img
                    src="/images/ecover-logo-new.png"
                    alt="Ecover"
                    className="h-full w-auto object-contain"
                    style={{ maxWidth: '180px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Amar Stars
            </h2>
            <p className="text-xl text-gray-600">
              Excellence in every aspect of brand distribution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {highlights.map((highlight, index) =>
            <Card
              key={index}
              className="border hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-gray-200 !py-[30px] !px-[32px] rounded-xl shadow text-card-foreground bg-white">

                <div className="text-black mb-4">
                  {highlight.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {highlight.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {highlight.description}
                </p>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Featured Brands */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our Featured Brands
            </h2>
            <p className="text-xl text-gray-400">
              Curated excellence from around the world
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {brands.map((brand, index) =>
            <Link
              key={index}
              to={brand.link}
              className="group">

                <div className="relative overflow-hidden rounded-2xl bg-gray-900 transition-transform duration-500 hover:scale-105">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                    src={brand.image}
                    alt={brand.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />

                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 space-y-2">
                    <p className="text-sm text-gray-400 font-medium uppercase tracking-wide">
                      {brand.tagline}
                    </p>
                    <h3 className="text-3xl font-bold">
                      {brand.name}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {brand.description}
                    </p>
                    <div className="flex items-center text-white pt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="font-medium">Learn More</span>
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/brands">
              <Button className="bg-white text-black hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105">
                View All Brands
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Amar Stars */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Why Amar Stars
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Amar Stars LLC is a curator of global beauty, lifestyle, and wellness brands in Mongolia, delivering quality, design, and trusted brand experiences since 2010.
              </p>
              
              <div className="space-y-6">
                {whyAmarStars.map((item, index) =>
                <div key={index} className="flex items-start space-x-4">
                    <CheckCircle2 className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/about">
                <Button className="bg-black text-white hover:bg-gray-800 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 mt-8">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?w=800&q=80"
                alt="Premium retail experience"
                className="rounded-2xl shadow-2xl" />

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Partner with Amar Stars
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Interested in bringing your brand to Mongolia or exploring partnership opportunities?
          </p>
          <Link to="/contact">
            <Button className="bg-white text-black hover:bg-gray-100 rounded-full px-10 py-7 text-xl font-semibold transition-all duration-300 hover:scale-105">
              Get in Touch
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>);

};

export default Home;